/**
 * Attribute-only layer snapshot helpers for AI chat payloads. Geometry is
 * omitted and history snapshots are dropped so POST /disasterchat stays under
 * COMS bodyParser (1mb).
 */

/** Soft budget for current-turn featureSnapshot JSON across all selected layers. */
export const FEATURE_SNAPSHOT_BYTE_BUDGET = 400_000;

/** Attribute-only internal row — geometry omitted. */
export function internalRowToAttributeSnapshot(row: {
  id: string;
  name?: string | null;
  description?: string | null;
  resourceType?: string | null;
  properties?: unknown;
}): Record<string, unknown> {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    resourceType: row.resourceType,
    properties: row.properties,
  };
}

/** Attribute-only external feature — no geometry. */
export function graphicToAttributeSnapshot(graphic: {
  attributes?: Record<string, unknown> | null;
}): { attributes: Record<string, unknown> } {
  return {
    attributes: { ...(graphic.attributes ?? {}) },
  };
}

/** Remove geometry keys from a single snapshot feature. */
export function stripFeatureGeometry(feature: unknown): unknown {
  if (!feature || typeof feature !== 'object' || Array.isArray(feature)) {
    return feature;
  }
  const record = feature as Record<string, unknown>;
  const { geometry: _geometry, ...rest } = record;
  return rest;
}

/**
 * Strip geometry from layer feature snapshots in realtimeSelections before
 * POST. Belt-and-suspenders against accidental geometry reintroduction.
 */
export function stripGeometriesFromRealtimeSelections<T>(selections: T): T {
  if (!Array.isArray(selections)) return selections;
  return selections.map((sel) => {
    if (!sel || typeof sel !== 'object') return sel;
    const entry = sel as Record<string, unknown>;
    if (entry.source !== 'Layers' || !Array.isArray(entry.selectedLayers)) {
      return sel;
    }
    return {
      ...entry,
      selectedLayers: entry.selectedLayers.map((layer) => {
        if (!layer || typeof layer !== 'object') return layer;
        const layerRec = layer as Record<string, unknown>;
        if (!Array.isArray(layerRec.featureSnapshot)) return layer;
        return {
          ...layerRec,
          featureSnapshot: layerRec.featureSnapshot.map((feature) =>
            stripFeatureGeometry(feature)
          ),
        };
      }),
    };
  }) as T;
}

/** Drop featureSnapshot from Layers selections (keep id/name/coverage metadata). */
export function dropFeatureSnapshotsFromSelections<T>(selections: T): T {
  if (!Array.isArray(selections)) return selections;
  return selections.map((sel) => {
    if (!sel || typeof sel !== 'object') return sel;
    const entry = sel as Record<string, unknown>;
    if (entry.source !== 'Layers' || !Array.isArray(entry.selectedLayers)) {
      return sel;
    }
    return {
      ...entry,
      selectedLayers: entry.selectedLayers.map((layer) => {
        if (!layer || typeof layer !== 'object') return layer;
        const layerRec = layer as Record<string, unknown>;
        if (!('featureSnapshot' in layerRec)) return layer;
        const { featureSnapshot: _snap, ...rest } = layerRec;
        return rest;
      }),
    };
  }) as T;
}

function jsonByteLength(value: unknown): number {
  try {
    return new TextEncoder().encode(JSON.stringify(value)).length;
  } catch {
    return Number.POSITIVE_INFINITY;
  }
}

/**
 * Legend-filtered complete snapshots are the fast path for filter/rank —
 * protect them from budget truncation while unprotected layer dumps are trimmed
 * first.
 */
export function isProtectedFilteredCompleteSnapshot(layer: unknown): boolean {
  if (!layer || typeof layer !== 'object') return false;
  const rec = layer as Record<string, unknown>;
  if (rec.featureSnapshotCoverage !== 'complete') return false;
  if (!Array.isArray(rec.featureSnapshot)) return false;
  return Array.isArray(rec.attributeFilters) && rec.attributeFilters.length > 0;
}

function dropSnapshotKeepMeta(
  layerRec: Record<string, unknown>
): Record<string, unknown> {
  const { featureSnapshot: _snap, ...rest } = layerRec;
  return rest;
}

/**
 * Enforce a byte budget on current-turn layer featureSnapshots. Truncates
 * features proportionally and marks coverage as sample, or drops snapshots
 * entirely if even a tiny sample won't fit.
 *
 * Protected layers (attributeFilters + complete coverage) are kept intact while
 * unprotected snapshots are dropped/truncated first.
 */
export function budgetFeatureSnapshotsInSelections<T>(
  selections: T,
  budgetBytes: number = FEATURE_SNAPSHOT_BYTE_BUDGET
): T {
  if (!Array.isArray(selections)) return selections;
  const withoutGeom = stripGeometriesFromRealtimeSelections(selections);
  const layersSel = withoutGeom.find(
    (sel) =>
      sel &&
      typeof sel === 'object' &&
      (sel as { source?: string }).source === 'Layers'
  ) as { selectedLayers?: unknown[] } | undefined;
  if (!layersSel || !Array.isArray(layersSel.selectedLayers)) {
    return withoutGeom;
  }

  const layers = layersSel.selectedLayers.map((layer) => {
    if (!layer || typeof layer !== 'object') return layer;
    const layerRec = layer as Record<string, unknown>;
    if (!Array.isArray(layerRec.featureSnapshot)) return layer;
    return {
      ...layerRec,
      featureSnapshot: layerRec.featureSnapshot.map((f) =>
        stripFeatureGeometry(f)
      ),
    };
  });

  const totalFeatures = layers.reduce<number>((sum, layer) => {
    if (!layer || typeof layer !== 'object') return sum;
    const snap = (layer as { featureSnapshot?: unknown[] }).featureSnapshot;
    return sum + (Array.isArray(snap) ? snap.length : 0);
  }, 0);

  if (totalFeatures === 0) {
    return withoutGeom;
  }

  const measure = (nextLayers: unknown[]) =>
    jsonByteLength(
      withoutGeom.map((sel) =>
        sel &&
        typeof sel === 'object' &&
        (sel as { source?: string }).source === 'Layers'
          ? { ...(sel as object), selectedLayers: nextLayers }
          : sel
      )
    );

  const withLayers = (nextLayers: unknown[]) =>
    withoutGeom.map((sel) =>
      sel &&
      typeof sel === 'object' &&
      (sel as { source?: string }).source === 'Layers'
        ? { ...(sel as object), selectedLayers: nextLayers }
        : sel
    ) as T;

  if (measure(layers) <= budgetBytes) {
    return withLayers(layers);
  }

  // Drop unprotected snapshots first; keep legend-filtered complete ones intact.
  const unprotectedDropped = layers.map((layer) => {
    if (!layer || typeof layer !== 'object') return layer;
    const layerRec = layer as Record<string, unknown>;
    if (!Array.isArray(layerRec.featureSnapshot)) return layer;
    if (isProtectedFilteredCompleteSnapshot(layerRec)) return layer;
    return dropSnapshotKeepMeta(layerRec);
  });

  if (measure(unprotectedDropped) <= budgetBytes) {
    return withLayers(unprotectedDropped);
  }

  // Truncate only unprotected layers (protected stay complete).
  const unprotectedFeatureCount = layers.reduce<number>((sum, layer) => {
    if (!layer || typeof layer !== 'object') return sum;
    if (isProtectedFilteredCompleteSnapshot(layer)) return sum;
    const snap = (layer as { featureSnapshot?: unknown[] }).featureSnapshot;
    return sum + (Array.isArray(snap) ? snap.length : 0);
  }, 0);

  const truncateUnprotectedTo = (maxUnprotectedFeatures: number): unknown[] => {
    let remaining = maxUnprotectedFeatures;
    return layers.map((layer) => {
      if (!layer || typeof layer !== 'object') return layer;
      const layerRec = layer as Record<string, unknown>;
      const snap = layerRec.featureSnapshot;
      if (!Array.isArray(snap)) return layer;
      if (isProtectedFilteredCompleteSnapshot(layerRec)) return layer;
      if (remaining <= 0) {
        return {
          ...dropSnapshotKeepMeta(layerRec),
          featureSnapshotCoverage: 'sample',
        };
      }
      const take = Math.min(snap.length, remaining);
      remaining -= take;
      return {
        ...layerRec,
        featureSnapshot: snap.slice(0, take),
        featureSnapshotCoverage:
          take < snap.length ? 'sample' : layerRec.featureSnapshotCoverage,
      };
    });
  };

  if (unprotectedFeatureCount > 0) {
    let lo = 0;
    let hi = unprotectedFeatureCount;
    let bestUnprotected: unknown[] | null = null;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      const candidate = truncateUnprotectedTo(mid);
      if (measure(candidate) <= budgetBytes) {
        bestUnprotected = candidate;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    if (bestUnprotected && measure(bestUnprotected) <= budgetBytes) {
      return withLayers(bestUnprotected);
    }
  }

  // Last resort: budget all layers including protected (previous behavior).
  let lo = 0;
  let hi = totalFeatures;
  let bestLayers: unknown[] | null = null;

  const truncateAllTo = (maxFeatures: number): unknown[] => {
    let remaining = maxFeatures;
    return layers.map((layer) => {
      if (!layer || typeof layer !== 'object') return layer;
      const layerRec = layer as Record<string, unknown>;
      const snap = layerRec.featureSnapshot;
      if (!Array.isArray(snap)) return layer;
      if (remaining <= 0) {
        return {
          ...dropSnapshotKeepMeta(layerRec),
          featureSnapshotCoverage: 'sample',
        };
      }
      const take = Math.min(snap.length, remaining);
      remaining -= take;
      return {
        ...layerRec,
        featureSnapshot: snap.slice(0, take),
        featureSnapshotCoverage:
          take < snap.length ? 'sample' : layerRec.featureSnapshotCoverage,
      };
    });
  };

  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const candidate = truncateAllTo(mid);
    if (measure(candidate) <= budgetBytes) {
      bestLayers = candidate;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }

  if (!bestLayers || measure(bestLayers) > budgetBytes) {
    return dropFeatureSnapshotsFromSelections(withoutGeom);
  }

  return withLayers(bestLayers);
}

export type CompactDisasterChatPayloadInput = {
  realtimeSelections: unknown;
  messages: unknown;
};

export type CompactDisasterChatPayloadResult = {
  realtimeSelections: unknown;
  messages: unknown;
  /** Selections safe to store on the local USER message (no featureSnapshot). */
  historyRealtimeSelections: unknown;
};

/**
 * Compact the outbound disasterchat POST:
 *
 * - Strip geometries everywhere
 * - Drop featureSnapshot from historical messages (prevents 39–55MB replays)
 * - Budget current-turn attribute snapshots (~400KB)
 * - Keep ephemeral arcgisUserToken on current-turn only (stripped from history)
 */
export function compactDisasterChatPayload(
  input: CompactDisasterChatPayloadInput,
  budgetBytes: number = FEATURE_SNAPSHOT_BYTE_BUDGET
): CompactDisasterChatPayloadResult {
  const realtimeSelections = budgetFeatureSnapshotsInSelections(
    stripGeometriesFromRealtimeSelections(input.realtimeSelections),
    budgetBytes
  );

  const messages = Array.isArray(input.messages)
    ? input.messages.map((msg) => {
        if (!msg || typeof msg !== 'object') return msg;
        const rec = msg as Record<string, unknown>;
        if (!('realtimeSelections' in rec)) return msg;
        return {
          ...rec,
          realtimeSelections: stripArcGisUserTokensFromSelections(
            dropFeatureSnapshotsFromSelections(
              stripGeometriesFromRealtimeSelections(rec.realtimeSelections)
            )
          ),
        };
      })
    : input.messages;

  return {
    realtimeSelections,
    messages,
    historyRealtimeSelections: stripArcGisUserTokensFromSelections(
      dropFeatureSnapshotsFromSelections(realtimeSelections)
    ),
  };
}

/**
 * Remove ephemeral ArcGIS tokens from stored/history selections (never
 * persist).
 */
export function stripArcGisUserTokensFromSelections<T>(selections: T): T {
  if (!Array.isArray(selections)) return selections;
  return selections.map((sel) => {
    if (!sel || typeof sel !== 'object') return sel;
    const entry = sel as Record<string, unknown>;
    if (entry.source !== 'Layers' || !Array.isArray(entry.selectedLayers)) {
      return sel;
    }
    return {
      ...entry,
      selectedLayers: entry.selectedLayers.map((layer) => {
        if (!layer || typeof layer !== 'object') return layer;
        const layerRec = layer as Record<string, unknown>;
        if (!('arcgisUserToken' in layerRec)) return layer;
        const { arcgisUserToken: _t, ...rest } = layerRec;
        return rest;
      }),
    };
  }) as T;
}
